import React from 'react';
import {
  Box,
  Field,
  HStack,
  Image,
  Menu,
  Portal,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as InputErrorIcon } from 'assets/icons/input_error.svg';
import { colors } from 'theme';
import { Wallet } from 'types';

const surfaceBorder = 'rgba(255,255,255,0.08)';

/** Native scrollbars (esp. Windows) stay light unless themed; this aligns the open list. */
const menuListScrollCss = {
  'color-scheme': 'dark',
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.28) rgba(255,255,255,0.06)',
  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.28)',
    borderRadius: '6px',
    border: '2px solid rgba(0,0,0,0.15)',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
} as const;

type ExchangeWalletSelectProps = {
  label: string;
  error?: string;
  isRequired?: boolean;
  wallets: Wallet[];
  value: string;
  onChange: (_walletId: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Omit field label (e.g. page header wallet picker). */
  hideLabel?: boolean;
};

const ExchangeWalletSelect: React.FC<ExchangeWalletSelectProps> = ({
  label,
  error,
  isRequired = false,
  wallets,
  value,
  onChange,
  isLoading = false,
  placeholder = 'Select wallet',
  disabled = false,
  hideLabel = false,
}) => {
  const selected = wallets.find((w) => w.id === value);

  return (
    <Field.Root invalid={Boolean(error)} required={isRequired}>
      {!hideLabel ? (
        <Field.Label
          fontWeight="medium"
          color={error ? 'error' : 'secondary2'}
          mb={2}
        >
          {label}
        </Field.Label>
      ) : null}
      <Box position="relative" w="100%">
        <Box position="relative" w="100%" mb={error ? 6 : hideLabel ? 0 : 4}>
          {isLoading && (
            <Stack
              pos="absolute"
              right={4}
              top="50%"
              transform="translateY(-50%)"
              zIndex={1}
              pointerEvents="none"
            >
              <Spinner size="sm" borderWidth="2px" />
            </Stack>
          )}
          <Menu.Root>
            <Menu.Trigger
              unstyled
              type="button"
              disabled={disabled || isLoading}
              width="100%"
              borderRadius="lg"
              minH="11"
              px={3}
              py={2}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              cursor={disabled ? 'not-allowed' : 'pointer'}
              opacity={disabled ? 0.6 : 1}
              color="body"
              bg="secondary"
              borderWidth="1px"
              borderStyle="solid"
              borderColor={error ? 'error' : surfaceBorder}
              _hover={{
                bg: disabled ? 'secondary' : 'rgba(255,255,255,0.05)',
              }}
              _focusVisible={{
                outline: 'none',
                boxShadow: '0 0 0 2px rgba(62, 207, 142, 0.45)',
              }}
              css={{
                backgroundColor: `${colors.secondary} !important`,
                color: `${colors.body} !important`,
              }}
            >
              {selected ? (
                <HStack gap={2} w="100%" justify="flex-start" align="center">
                  <Image
                    boxSize={7}
                    rounded="full"
                    src={selected.currency.icon}
                    alt=""
                    flexShrink={0}
                    objectFit="cover"
                  />
                  <Text
                    fontSize="md"
                    fontWeight="medium"
                    color="body"
                    lineClamp={1}
                    flex={1}
                    minW={0}
                    textAlign="left"
                  >
                    {selected.currency.title}
                  </Text>
                </HStack>
              ) : (
                <Text fontSize="md" color="body2" textAlign="left">
                  {placeholder}
                </Text>
              )}
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  maxH="280px"
                  overflowY="auto"
                  bg="dark"
                  borderWidth="1px"
                  borderColor={surfaceBorder}
                  borderRadius="lg"
                  py={2}
                  px={1}
                  boxShadow="0 12px 40px rgba(0,0,0,0.45)"
                  css={menuListScrollCss}
                >
                  {wallets.map((wallet) => (
                    <Menu.Item
                      key={wallet.id}
                      value={wallet.id}
                      minH="10"
                      onSelect={() => onChange(wallet.id)}
                      py={3}
                      px={4}
                      borderRadius="md"
                      _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                    >
                      <HStack gap={2} flex={1} w="100%">
                        <Image
                          boxSize={6}
                          rounded="full"
                          src={wallet.currency.icon}
                          alt=""
                          flexShrink={0}
                          objectFit="cover"
                        />
                        <Text fontSize="sm" color="body">
                          {wallet.currency.title}
                        </Text>
                      </HStack>
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Box>
        {error ? (
          <Box
            color="error"
            fontSize="sm"
            textAlign="start"
            display="flex"
            alignItems="center"
            gap={1}
            pos="absolute"
            left={0}
            bottom={0}
          >
            <InputErrorIcon />
            {error}
          </Box>
        ) : null}
      </Box>
    </Field.Root>
  );
};

export default ExchangeWalletSelect;
